import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, Eye, EyeOff, KeyRound, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";

import { DashboardShell } from "@/components/dravian/dashboard-shell";
import { Eyebrow } from "@/components/dravian/eyebrow";
import {
  license as licenseApi,
  llmCredentials as credApi,
  type LlmCredential,
  type LlmProviderName,
} from "@/lib/api";

export const Route = createFileRoute("/settings")({
  head: () => ({ meta: [{ title: "Settings — Dravian Horizon" }] }),
  component: GlobalSettings,
});

const PROVIDERS: { id: LlmProviderName; label: string; hint: string }[] = [
  { id: "anthropic", label: "Anthropic", hint: "Claude family (Opus, Sonnet, Haiku)" },
  { id: "openai", label: "OpenAI", hint: "GPT family (4o, 4 Turbo, …)" },
  { id: "azure_openai", label: "Azure OpenAI", hint: "Azure-hosted OpenAI deployments" },
  { id: "bedrock", label: "AWS Bedrock", hint: "Claude/Llama/Titan via Bedrock" },
];

function GlobalSettings() {
  const qc = useQueryClient();

  const credsQuery = useQuery({
    queryKey: ["llm-credentials"],
    queryFn: () => credApi.list(),
  });
  const licenseQuery = useQuery({
    queryKey: ["license-status"],
    queryFn: () => licenseApi.status(),
    staleTime: 60_000,
  });

  const credsByProvider = useMemo(() => {
    const m = new Map<LlmProviderName, LlmCredential>();
    for (const c of credsQuery.data ?? []) m.set(c.provider as LlmProviderName, c);
    return m;
  }, [credsQuery.data]);

  const activeProvider =
    (credsQuery.data ?? []).find((c) => c.is_active)?.provider ?? "anthropic";

  const [selectedProvider, setSelectedProvider] = useState<LlmProviderName>(
    activeProvider as LlmProviderName,
  );
  // When the cache resolves, sync the picker once. Otherwise keep the user's choice.
  const [pickerSynced, setPickerSynced] = useState(false);
  if (!pickerSynced && credsQuery.isSuccess) {
    setPickerSynced(true);
    setSelectedProvider(activeProvider as LlmProviderName);
  }

  const [apiKey, setApiKey] = useState("");
  const [show, setShow] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(
    null,
  );

  const existing = credsByProvider.get(selectedProvider);

  const save = useMutation({
    mutationFn: () =>
      credApi.upsert({
        provider: selectedProvider,
        api_key: apiKey,
        activate: true,
      }),
    onSuccess: () => {
      setApiKey("");
      setTestResult(null);
      qc.invalidateQueries({ queryKey: ["llm-credentials"] });
    },
  });

  const activate = useMutation({
    mutationFn: () => credApi.activate(selectedProvider),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["llm-credentials"] }),
  });

  const test = useMutation({
    mutationFn: () => credApi.test({ provider: selectedProvider, api_key: apiKey }),
    onSuccess: (res) => {
      setTestResult({
        ok: res.ok,
        msg: res.ok ? `OK · ${res.model ?? "model"}` : res.error ?? "failed",
      });
    },
  });

  return (
    <DashboardShell>
      <main className="flex-1 mx-auto w-full max-w-[820px] px-6 py-10 space-y-4">
        <div>
          <div className="label-eyebrow mb-1">Settings</div>
          <h1 className="text-[28px] font-semibold tracking-tight">Deployment Settings</h1>
        </div>

        {/* LLM Provider */}
        <section className="panel p-5 space-y-4">
          <div className="flex items-center justify-between">
            <Eyebrow>LLM Provider (BYOK)</Eyebrow>
            <button
              onClick={() => credsQuery.refetch()}
              className="text-muted-foreground hover:text-foreground"
              title="Refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="text-[12px] text-muted-foreground">
            Bring your own key. Stored encrypted at rest. The chat uses the active
            provider; switching providers is instant — no restart required.
          </p>

          <div>
            <div className="label-eyebrow mb-1.5">Provider</div>
            <div className="grid grid-cols-4 gap-2">
              {PROVIDERS.map((p) => {
                const has = credsByProvider.has(p.id);
                const isActive = credsByProvider.get(p.id)?.is_active;
                const selected = selectedProvider === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => {
                      setSelectedProvider(p.id);
                      setTestResult(null);
                    }}
                    className={`text-left rounded-md border px-3 py-2.5 text-[12px] transition ${
                      selected
                        ? "border-primary/50 bg-primary/10 text-foreground"
                        : "border-border bg-surface-elevated/40 text-muted-foreground hover:border-primary/30"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="font-medium">{p.label}</span>
                      {isActive && (
                        <span className="text-[9px] uppercase tracking-wider text-primary">
                          active
                        </span>
                      )}
                      {has && !isActive && (
                        <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
                          saved
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground leading-snug">
                      {p.hint}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="label-eyebrow mb-1.5">
              {existing ? "Replace API Key" : "API Key"}
            </div>
            {existing && (
              <div className="mb-2 text-[11px] text-muted-foreground">
                A key ending in <span className="font-mono">•••• {existing.last4}</span>{" "}
                is already saved. Submit a new value to replace it.
              </div>
            )}
            <div className="flex items-center gap-2 rounded-md border border-border bg-surface-elevated px-3 py-2 focus-within:border-primary/40">
              <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                type={show ? "text" : "password"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={
                  selectedProvider === "anthropic"
                    ? "sk-ant-…"
                    : selectedProvider === "openai"
                      ? "sk-…"
                      : selectedProvider === "bedrock"
                        ? "AKIA…:secret-key"
                        : "your-api-key"
                }
                className="flex-1 bg-transparent text-[13px] outline-none font-mono"
              />
              <button
                onClick={() => setShow((v) => !v)}
                className="text-muted-foreground hover:text-foreground"
                title={show ? "Hide" : "Show"}
                type="button"
              >
                {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </div>
            {testResult && (
              <div
                className={`mt-2 text-[11px] ${
                  testResult.ok ? "text-risk-low" : "text-risk-high"
                }`}
              >
                {testResult.msg}
              </div>
            )}
            {save.isError && (
              <div className="mt-2 text-[11px] text-risk-high">
                {String(save.error)}
              </div>
            )}
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={() => test.mutate()}
                disabled={!apiKey.trim() || test.isPending}
                className="rounded-md border border-primary/40 bg-primary/15 text-primary px-3 py-1.5 text-[12px] font-medium disabled:opacity-50"
              >
                {test.isPending ? "Testing…" : "Test connection"}
              </button>
              <button
                onClick={() => save.mutate()}
                disabled={!apiKey.trim() || save.isPending}
                className="rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-medium disabled:opacity-50"
              >
                {save.isPending ? "Saving…" : "Save and activate"}
              </button>
              {existing && !existing.is_active && (
                <button
                  onClick={() => activate.mutate()}
                  disabled={activate.isPending}
                  className="rounded-md border border-border bg-surface px-3 py-1.5 text-[12px] text-foreground/85 hover:bg-surface-elevated"
                >
                  Use saved key
                </button>
              )}
            </div>
          </div>
        </section>

        {/* License */}
        <section className="panel p-5">
          <Eyebrow>License</Eyebrow>
          {licenseQuery.data ? (
            <div className="grid grid-cols-2 gap-4 text-[13px]">
              <div>
                <div className="label-eyebrow mb-1.5">Organization</div>
                <div className="text-foreground/95">
                  {licenseQuery.data.organization_name ?? "—"}
                </div>
                <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                  {licenseQuery.data.organization_id?.slice(0, 8) ?? ""}
                </div>
              </div>
              <div>
                <div className="label-eyebrow mb-1.5">Status</div>
                {licenseQuery.data.state === "active" && (
                  <span className="inline-flex items-center gap-1.5 text-risk-low text-[12px]">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Active
                    {licenseQuery.data.expires_at &&
                      ` until ${new Date(licenseQuery.data.expires_at).toLocaleDateString()}`}
                  </span>
                )}
                {licenseQuery.data.state === "grace" && (
                  <span className="text-risk-medium text-[12px]">
                    Grace period · ends{" "}
                    {licenseQuery.data.grace_until
                      ? new Date(licenseQuery.data.grace_until).toLocaleDateString()
                      : "soon"}
                  </span>
                )}
                {licenseQuery.data.state === "needs_activation" && (
                  <span className="text-risk-high text-[12px]">Activation required</span>
                )}
                {licenseQuery.data.state === "degraded" && (
                  <span className="text-risk-high text-[12px]">
                    License Service unreachable
                  </span>
                )}
              </div>
              <div>
                <div className="label-eyebrow mb-1.5">Tier</div>
                <div className="text-foreground/95 capitalize">
                  {licenseQuery.data.subscription_tier ?? "—"}
                </div>
              </div>
              <div>
                <div className="label-eyebrow mb-1.5">Features</div>
                <div className="flex flex-wrap gap-1">
                  {(licenseQuery.data.features ?? []).length === 0 && (
                    <span className="text-[11px] text-muted-foreground italic">
                      None active
                    </span>
                  )}
                  {(licenseQuery.data.features ?? []).map((f) => (
                    <span
                      key={f}
                      className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border bg-surface-elevated/40 px-1.5 py-0.5 rounded font-mono"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : licenseQuery.isLoading ? (
            <div className="text-[12px] text-muted-foreground">Loading…</div>
          ) : (
            <div className="text-[12px] text-risk-high">
              Couldn't load license info.
            </div>
          )}
        </section>

        <section className="panel p-5">
          <Eyebrow>Appearance</Eyebrow>
          <p className="text-[12px] text-muted-foreground">
            Dravian Horizon ships dark-only by design. Light theme is not available.
          </p>
        </section>
      </main>
    </DashboardShell>
  );
}
